// ./src/components/generic/MediaSelector/tabs/StockMediaTab.tsx
//
// Search a stock-media provider (Unsplash / GIPHY / Pexels) and pick a result.
// Reusable for any source — the parent MediaSelector passes the source + kind and
// handles the chosen item (storing its URL + firing the provider's attribution
// hook). Selecting a result yields a URL (no uploaded_file_id).

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Stack, TextInput, Button, Text, Box, Center, Loader, ActionIcon, Alert, Anchor, SegmentedControl, Group, useMatches } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useTranslation } from "react-i18next";

import { useBranding } from "@/contexts/BrandingContext";

import { AppIcon } from "../../AppIcon";
import { EmptyState } from "../../EmptyState";
import { MediaKind } from "../mediaSelectorConfig";
import { StockShape, StockSource, StockMediaItem, searchStockMedia, stockServiceInfo, stockShapeSupported } from "../stockMedia";
import { masonryColumns, tileRatio } from "../stockShape";

export interface StockMediaTabProps {
  source: StockSource;
  kind: MediaKind;
  /** Shared search text — persists across tabs (lifted to the MediaSelector). */
  query: string;
  onQueryChange: (q: string) => void;
  /** #95 — the picture shape asked for (shared across the stock tabs, like the query). Ignored by a service without one. */
  shape: StockShape;
  onShapeChange: (shape: StockShape) => void;
  /** URL of the currently-picked item (drives the selected highlight). */
  selectedUrl: string | null;
  /** Multi-select: highlight every url in this set + toggle on click. */
  multiSelect?: boolean;
  selectedUrls?: string[];
  /** Called with the chosen item, or null when deselected. */
  onPick: (item: StockMediaItem | null) => void;
}

const SOURCE_LABEL: Record<StockSource, string> = { unsplash: "Unsplash", giphy: "GIPHY", pexels: "Pexels" };

/** #95 — a tile is drawn in its picture's own shape (a masonry of columns), so a tall photo looks tall before it is picked. */
const ratioOf = (item: StockMediaItem): number => tileRatio(item.width, item.height);

export const StockMediaTab: React.FC<StockMediaTabProps> = ({ source, kind, query, onQueryChange, shape, onShapeChange, selectedUrl, multiSelect = false, selectedUrls = [], onPick }) => {
  const { t } = useTranslation(["common"]);
  const inputRef = useRef<HTMLInputElement>(null);

  const [submitted, setSubmitted] = useState("");
  const [items, setItems] = useState<StockMediaItem[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const callRef = useRef(0);
  const searchedRef = useRef(""); // last query actually searched

  const label = SOURCE_LABEL[source];
  // A2e #101 (i) — Unsplash's referral source is this dashboard's SPACE (its white label's).
  const service = stockServiceInfo(source, useBranding().whiteLabel?.spaceId);
  const canShape = stockShapeSupported(source);
  // What this service is actually asked: a shape only where it can filter by one.
  const askedShape: StockShape = canShape ? shape : "any";
  const columns = useMatches({ base: 2, sm: 3, md: 4 });
  const columnItems = masonryColumns(items, columns, ratioOf);

  const runSearch = useCallback(
    async (q: string, nextPage: number) => {
      const my = ++callRef.current;
      setLoading(true);
      const result = await searchStockMedia(source, q, nextPage, kind === "video" ? "video" : "image", askedShape);
      if (my !== callRef.current) {
        return;
      }
      setLoading(false);
      setError(Boolean(result.error));
      setItems((prev) => (nextPage === 1 ? result.items : [...prev, ...result.items]));
      setHasMore(result.hasMore);
      setPage(nextPage);
    },
    [source, kind, askedShape]
  );

  const doSearch = useCallback(
    (q: string) => {
      const trimmed = q.trim();
      // ⚠ The SHAPE is part of what was searched: a new shape with the same words must search again.
      const searchKey = `${trimmed}|${askedShape}`;
      if (!trimmed || searchKey === searchedRef.current) {
        return;
      }
      searchedRef.current = searchKey;
      setSubmitted(trimmed);
      onPick(null);
      runSearch(trimmed, 1);
    },
    [runSearch, onPick, askedShape]
  );

  const submit = useCallback(() => doSearch(query), [doSearch, query]);

  // Search-as-you-type: fire 500ms after the user stops typing. `useDebouncedValue`
  // returns the initial value immediately, so this also runs the SHARED query on
  // mount — keeping the search alive when hopping between tabs.
  const [debouncedQuery] = useDebouncedValue(query, 500);
  useEffect(() => {
    doSearch(debouncedQuery);
  }, [debouncedQuery, doSearch]);

  // Focus the search box on mount.
  useEffect(() => {
    const id = window.setTimeout(() => inputRef.current?.focus(), 50);
    return () => window.clearTimeout(id);
  }, []);

  const pick = (item: StockMediaItem) => {
    // Multi-select: always emit so the parent toggles membership.
    if (multiSelect) {
      onPick(item);
      return;
    }
    if (selectedUrl && item.fullUrl === selectedUrl) {
      onPick(null);
    } else {
      onPick(item);
    }
  };

  const isSelected = (item: StockMediaItem): boolean =>
    multiSelect ? selectedUrls.includes(item.fullUrl) : !!selectedUrl && item.fullUrl === selectedUrl;

  return (
    <Stack gap="sm">
      <TextInput
        ref={inputRef}
        size="md"
        label={t("common:mediaSelector.stockSearchLabel", { defaultValue: "Search {{source}}", source: label })}
        placeholder={t("common:mediaSelector.stockSearchPlaceholder", { defaultValue: "Try 'birthday', 'garden party'…" })}
        value={query}
        onChange={(e) => onQueryChange(e.currentTarget.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            submit();
          }
        }}
        leftSection={loading && page === 1 ? <Loader size={16} /> : <AppIcon iconName="Search" size={16} />}
        rightSection={
          query ? (
            <ActionIcon variant="subtle" color="gray" onClick={() => onQueryChange("")} aria-label="Clear">
              <AppIcon iconName="X" size={14} />
            </ActionIcon>
          ) : undefined
        }
      />

      {canShape && (
        <SegmentedControl
          size="md"
          fullWidth
          value={shape}
          onChange={(v) => onShapeChange(v as StockShape)}
          aria-label={t("common:mediaSelector.stockShape", { defaultValue: "Shape" })}
          data={[
            { value: "any", label: t("common:mediaSelector.shapeAny", { defaultValue: "Any" }) },
            { value: "portrait", label: t("common:mediaSelector.shapePortrait", { defaultValue: "Vertical" }) },
            { value: "landscape", label: t("common:mediaSelector.shapeLandscape", { defaultValue: "Horizontal" }) },
            { value: "square", label: t("common:mediaSelector.shapeSquare", { defaultValue: "Square" }) },
          ]}
        />
      )}

      {error && (
        <Alert color="orange" variant="light" icon={<AppIcon iconName="AlertTriangle" size={18} />}>
          {t("common:mediaSelector.stockError", {
            source: label,
            defaultValue: "Couldn't reach {{source}}. The image proxy may need this site's address added to its allow-list.",
          })}
        </Alert>
      )}

      {/* Results */}
      {!submitted && (
        <EmptyState
          iconName={source === "giphy" ? "Movie" : "Photo"}
          title={t("common:mediaSelector.stockPrompt", { defaultValue: "Search {{source}}", source: label })}
          description={t("common:mediaSelector.stockPromptDesc", {
            defaultValue: "Type a few words above to find a great {{kind}}.",
            kind: kind === "video" ? "video" : "image",
          })}
        />
      )}

      {submitted && items.length === 0 && !loading && !error && (
        <EmptyState
          iconName="Search"
          title={t("common:mediaSelector.stockNoResults", { defaultValue: "No results" })}
          description={t("common:mediaSelector.stockNoResultsDesc", { defaultValue: "Try different keywords." })}
        />
      )}

      {items.length > 0 && (
        // #95 — columns (2 on a phone, 3 / 4 wider — as the grid was), each tile in its picture's own shape.
        <Group align="flex-start" gap={8} wrap="nowrap">
          {columnItems.map((column, ci) => (
            <Stack key={ci} gap={8} style={{ flex: 1, minWidth: 0 }}>
              {column.map((item) => {
                const selected = isSelected(item);
                return (
                  <Box
                    key={`${item.source}-${item.id}`}
                    role="button"
                    onClick={() => pick(item)}
                    style={{
                      position: "relative",
                      aspectRatio: `${ratioOf(item).toFixed(3)} / 1`,
                      borderRadius: 8,
                      overflow: "hidden",
                      cursor: "pointer",
                      border: selected ? "3px solid var(--mantine-color-blue-5)" : "1px solid var(--mantine-color-default-border)",
                      boxShadow: selected ? "0 0 0 2px var(--mantine-color-blue-2)" : undefined,
                      background: "var(--mantine-color-default-hover)",
                    }}
                  >
                    <img
                      src={item.thumbUrl}
                      alt={item.description ?? ""}
                      loading="lazy"
                      style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    />
                    {item.kind === "video" && (
                      <Box style={{ position: "absolute", right: 6, bottom: 6, background: "rgba(0,0,0,0.55)", borderRadius: 4, padding: "1px 4px", display: "flex" }}>
                        <AppIcon iconName="PlayerPlay" size={14} color="#fff" />
                      </Box>
                    )}
                    {selected && (
                      <Box style={{ position: "absolute", top: 4, right: 4, background: "var(--mantine-color-blue-6)", borderRadius: "50%", width: 22, height: 22, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <AppIcon iconName="Check" size={14} color="#fff" />
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Stack>
          ))}
        </Group>
      )}

      {loading && page > 1 && (
        <Center py="sm">
          <Loader size="sm" />
        </Center>
      )}

      {hasMore && !loading && (
        <Center>
          <Button variant="subtle" onClick={() => runSearch(submitted, page + 1)}>
            {t("common:buttons.loadMore", { defaultValue: "Load more" })}
          </Button>
        </Center>
      )}

      {items.length > 0 && (
        <Text size="xs" c="dimmed" ta="center">
          {t("common:mediaSelector.poweredBy", { defaultValue: "Powered by" })}{" "}
          <Anchor href={service.url} target="_blank" rel="noopener noreferrer" fw={600}>
            {label}
          </Anchor>
        </Text>
      )}
    </Stack>
  );
};
